import React from 'react';
import { Paper, Typography, Box, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTime } from 'luxon';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { UserRole } from '../../types/userProfile';
import ApproveDeclineTranslation from './ApproveDeclineTranslation';
import { openDeactivationDialog } from '../../store/slices/uiSlice';
import { selectDetailsViewModel } from '../../store/selectors/uiSelectors';
import ExpandableText from '../ExpandableText';
import { HISTORY_EVENT } from '../../models/eventTypes';

interface TranslationSuggestionCardProps {
  details: TranslationDetail;
}

const TranslationSuggestionCard: React.FC<TranslationSuggestionCardProps> = ({
  details,
}) => {
  const dispatch = useAppDispatch();
  const viewModel = useAppSelector(selectDetailsViewModel);
  const showApproveDecline = viewModel?.pendingCard.showsApproveDecline ?? false;
  const showDeactivate = viewModel?.pendingCard.showsDeactivateInsideCard ?? false;
  const currentUser = useAppSelector(state => state.userProfile.data);
    
  const { translationReview, history } = details;
  const latestEvent = history?.[0];
  const isEdited = latestEvent?.eventType === HISTORY_EVENT.Edited;

  const suggestionText = translationReview?.valueInReview || '';
  const author = translationReview?.author || '';
  const date = (isEdited ? latestEvent?.date : translationReview?.date) ?? new Date().toISOString();
  const description = translationReview?.description || '';
  const changeReason = translationReview?.changeReason || '';
  const dateHuman = DateTime.fromISO(date).toFormat('MM/dd/yyyy');
  const timeHuman = DateTime.fromISO(date).toFormat('t');

  const subtitle = isEdited
    ? `${latestEvent?.title || ''} on ${dateHuman} at ${timeHuman}`
    : `${description ? `${description} by ${author}` : author} on ${dateHuman} at ${timeHuman}`;

  const isApprovalPending =
    translationReview &&
    (currentUser?.role === UserRole.Contributor || currentUser?.role === UserRole.ViewOnly);

  return (
    <Paper elevation={0} sx={styles.mainContainer}>
       <Box sx={styles.suggestionText}>
        <ExpandableText 
          text={suggestionText} 
          fontStyle="italic"
        />
      </Box>
       {changeReason && <Typography variant="body2" sx={styles.changeReasonText}>
        {"Change reason: "+ changeReason}
      </Typography>}
      <Typography variant="body2" color="text.secondary" sx={styles.dateText}>
        {subtitle}
      </Typography>
      {isApprovalPending && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Button variant="text" disabled size="medium">
            APPROVAL PENDING
          </Button>
          {showDeactivate && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button onClick={() => dispatch(openDeactivationDialog())}>
                <DeleteIcon /> Deactivate Translation
              </Button>
            </Box>
          )}
        </Box>
      )}
     {showApproveDecline && (
        <ApproveDeclineTranslation
          translationRecordId={details.translationRecordId}
          language={details.language}
        />
      )}
    </Paper>
  );
};

export default TranslationSuggestionCard;

const styles = {
  mainContainer: { border: '1px solid #bdbdbd', borderRadius: 2, p: 2, mb: 2, bgcolor: 'grey.50' },
  suggestionText: { mb: 1 },
  changeReasonText: { mt: 2, mb: 1 },
  dateText: { mb: 2 },
};
